import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  RefreshCw,
  Loader2,
  ChevronRight,
  ChevronDown,
  Clock,
  User,
  CheckCircle,
  AlertCircle,
  FileText,
  Link,
  X,
  Check,
  ArrowRight,
  BarChart2,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

// ── State machine config ─────────────────────────────────────────────────────

const STATES = {
  open: {
    label: "Open",
    color: "bg-slate-100 text-slate-600",
    dot: "bg-slate-400",
  },
  assigned: {
    label: "Assigned",
    color: "bg-blue-100 text-blue-700",
    dot: "bg-blue-500",
  },
  researching: {
    label: "Researching",
    color: "bg-amber-100 text-amber-700",
    dot: "bg-amber-500",
  },
  drafted: {
    label: "Drafted",
    color: "bg-violet-100 text-violet-700",
    dot: "bg-violet-500",
  },
  approved: {
    label: "Approved",
    color: "bg-green-100 text-green-700",
    dot: "bg-green-500",
  },
  stale: {
    label: "Stale",
    color: "bg-rose-100 text-rose-700",
    dot: "bg-rose-500",
  },
};

// Valid transitions: { state: [{ action, label, nextState, color, requiresReview }] }
const TRANSITIONS = {
  open: [
    {
      action: "assign",
      label: "Assign to me",
      nextState: "assigned",
      color: "blue",
    },
  ],
  assigned: [
    {
      action: "research",
      label: "Start Researching",
      nextState: "researching",
      color: "amber",
    },
  ],
  researching: [
    {
      action: "draft",
      label: "Save Draft",
      nextState: "drafted",
      color: "violet",
      requiresDraft: true,
    },
  ],
  drafted: [
    {
      action: "approve",
      label: "Approve",
      nextState: "approved",
      color: "green",
      requiresReview: true,
    },
    {
      action: "reject",
      label: "Reject",
      nextState: "researching",
      color: "red",
      requiresReview: true,
    },
  ],
  approved: [],
  stale: [
    {
      action: "reverify",
      label: "Re-verify",
      nextState: "assigned",
      color: "blue",
    },
  ],
};

function timeInState(task) {
  const now = Date.now();
  const stateField =
    {
      assigned: task.assigned_at,
      researching: task.researching_at,
      drafted: task.drafted_at,
      approved: task.approved_at,
      stale: task.stale_at,
    }[task.state] || task.created_at;
  if (!stateField) return null;
  const ms = now - new Date(stateField).getTime();
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h`;
  return "< 1h";
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StateBadge({ state }) {
  const cfg = STATES[state] || STATES.open;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function TimeInStateBadge({ task }) {
  const t = timeInState(task);
  if (!t || task.state === "approved") return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">
      <Clock className="w-3 h-3" />
      {t}
    </span>
  );
}

// ── Research panel (side drawer) ─────────────────────────────────────────────

function ResearchPanel({ task, onClose, onTransition, isReviewer, headers }) {
  const [notes, setNotes] = useState(task.draft_answer || "");
  const [sourceName, setSourceName] = useState(task.draft_source_name || "");
  const [sourceUrl, setSourceUrl] = useState(task.draft_source_url || "");
  const [reviewComment, setReviewComment] = useState("");
  const [similar, setSimilar] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [saving, setSaving] = useState(false);
  const autosaveRef = useRef(null);

  // Autosave every 30s when notes change
  useEffect(() => {
    if (task.state !== "researching" && task.state !== "drafted") return;
    clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(() => {
      if (notes || sourceName || sourceUrl) saveDraft(false);
    }, 30000);
    return () => clearTimeout(autosaveRef.current);
  }, [notes, sourceName, sourceUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSimilar = async () => {
    setLoadingSimilar(true);
    try {
      const res = await fetch(
        `${AUTH_URL}/gap-tasks/search-kb?facility_id=${encodeURIComponent(task.facility_id)}&q=${encodeURIComponent(task.question)}`,
        { headers },
      );
      if (res.ok) setSimilar(await res.json());
    } catch {}
    setLoadingSimilar(false);
  };

  useEffect(() => {
    loadSimilar();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = async (showAlert = true) => {
    setSaving(true);
    try {
      const res = await fetch(`${AUTH_URL}/gap-tasks/${task.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          draft_answer: notes,
          draft_source_name: sourceName,
          draft_source_url: sourceUrl,
        }),
      });
      if (!res.ok && showAlert) alert("Failed to save draft.");
    } catch {}
    setSaving(false);
  };

  const handleTransition = async (tr) => {
    if (tr.requiresDraft && (!notes.trim() || !sourceName.trim())) {
      alert("A draft answer and source name are required before saving.");
      return;
    }
    if (tr.requiresDraft) await saveDraft(false);
    await onTransition(task.id, tr.action, {
      reviewer_comments: reviewComment,
    });
    onClose();
  };

  const transitions = TRANSITIONS[task.state] || [];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div className="flex-1 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <StateBadge state={task.state} />
              <TimeInStateBadge task={task} />
            </div>
            <p className="text-sm font-semibold text-slate-800 leading-snug">
              {task.question}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Surfaced {task.times_surfaced}× · facility: {task.facility_id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Similar KB content */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Similar KB Content
              </p>
              <button
                onClick={loadSimilar}
                disabled={loadingSimilar}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              >
                {loadingSimilar ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
                Refresh
              </button>
            </div>
            {similar.length === 0 ? (
              <p className="text-xs text-slate-400 italic">
                No similar KB entries found — this is a genuine gap.
              </p>
            ) : (
              <div className="space-y-2">
                {similar.slice(0, 5).map((s, i) => (
                  <div
                    key={i}
                    className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 border border-slate-100"
                  >
                    <p className="font-medium text-slate-600 mb-1">
                      {s.question || s.title}
                    </p>
                    <p className="text-slate-500 line-clamp-2">
                      {s.answer || s.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Research notes / draft answer */}
          {(task.state === "researching" || task.state === "drafted") && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Draft Answer
              </p>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={6}
                placeholder="Write the researched answer here. Do not use AI to generate this — it must be verified from a real source."
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-800 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Source Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    value={sourceName}
                    onChange={(e) => setSourceName(e.target.value)}
                    placeholder="e.g. GOP.gov, Meeting notes"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Source URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      value={sourceUrl}
                      onChange={(e) => setSourceUrl(e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    {sourceUrl && (
                      <a
                        href={sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        title="Open source URL to verify"
                        className="flex items-center px-2.5 py-2 bg-slate-100 hover:bg-blue-50 border border-slate-300 rounded-lg text-slate-500 hover:text-blue-600 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stale re-verification panel */}
          {task.state === "stale" && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 space-y-3">
              <p className="text-xs font-semibold text-rose-700 uppercase tracking-wide flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                Source Re-Verification Required
              </p>
              {task.draft_source_name && (
                <div className="text-sm text-slate-700">
                  <span className="font-medium">Source: </span>
                  {task.draft_source_name}
                </div>
              )}
              {task.draft_source_url ? (
                <div className="flex items-center gap-3">
                  <a
                    href={task.draft_source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-rose-300 text-rose-700 hover:bg-rose-100 rounded-lg text-sm font-medium transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Source URL
                  </a>
                  <p className="text-xs text-rose-600">
                    Confirm the source is still accessible and content is
                    accurate, then click Re-verify below.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-rose-600">
                  No source URL recorded. Reassign to add a verified source.
                </p>
              )}
              {task.draft_answer && (
                <div className="bg-white rounded p-3 border border-rose-100">
                  <p className="text-xs font-medium text-slate-500 mb-1">
                    Previous draft answer:
                  </p>
                  <p className="text-sm text-slate-700 line-clamp-3">
                    {task.draft_answer}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Read-only draft for review */}
          {task.state === "drafted" && isReviewer && task.draft_answer && (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Draft to Review
              </p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">
                {task.draft_answer}
              </p>
              {task.draft_source_name && (
                <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                  <FileText className="w-3.5 h-3.5" />
                  <span>{task.draft_source_name}</span>
                  {task.draft_source_url && (
                    <a
                      href={task.draft_source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Link className="w-3 h-3" /> View source
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Reviewer comment box */}
          {task.state === "drafted" && isReviewer && (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Reviewer Comments
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                rows={3}
                placeholder="Optional: notes for the steward if rejecting, or approval context."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Existing reviewer comments (if any) */}
          {task.reviewer_comments && task.state !== "drafted" && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-amber-700 mb-1">
                Reviewer Feedback
              </p>
              <p className="text-sm text-amber-800">{task.reviewer_comments}</p>
            </div>
          )}
        </div>

        {/* Action footer */}
        <div className="border-t px-5 py-4 flex items-center gap-3 flex-wrap">
          {task.state === "researching" && (
            <button
              onClick={() => saveDraft()}
              disabled={saving}
              className="px-4 py-2 text-sm border border-slate-300 hover:bg-slate-50 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Autosave
            </button>
          )}
          {transitions.map((tr) => (
            <button
              key={tr.action}
              onClick={() => handleTransition(tr)}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2 ${
                tr.color === "green"
                  ? "bg-green-600 hover:bg-green-700"
                  : tr.color === "red"
                    ? "bg-red-600 hover:bg-red-700"
                    : tr.color === "violet"
                      ? "bg-violet-600 hover:bg-violet-700"
                      : tr.color === "amber"
                        ? "bg-amber-600 hover:bg-amber-700"
                        : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {tr.action === "approve" ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <ArrowRight className="w-3.5 h-3.5" />
              )}
              {tr.label}
            </button>
          ))}
          <button
            onClick={onClose}
            className="ml-auto text-sm text-slate-500 hover:text-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const GapTasksSteward = () => {
  const { token, user, isManager } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [facilityId, setFacilityId] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [stateFilter, setStateFilter] = useState("all");
  const [myGapsOnly, setMyGapsOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTask, setActiveTask] = useState(null);
  const [transitioning, setTransitioning] = useState(new Set());
  const [stats, setStats] = useState(null);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  // Load facilities
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setFacilities(list);
          if (list.length > 0) setFacilityId(list[0].facility_id || "");
        }
      } catch {}
    };
    load();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTasks = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ facility_id: facilityId });
      if (stateFilter !== "all") params.append("state", stateFilter);
      if (myGapsOnly) params.append("assigned_to_me", "true");
      const res = await fetch(`${AUTH_URL}/gap-tasks?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTasks(
          Array.isArray(data.tasks)
            ? data.tasks
            : Array.isArray(data)
              ? data
              : [],
        );
        if (data.stats) setStats(data.stats);
      }
    } catch {}
    setLoading(false);
  }, [token, facilityId, stateFilter, myGapsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleTransition = async (taskId, action, extra = {}) => {
    setTransitioning((prev) => new Set([...prev, taskId]));
    try {
      const res = await fetch(`${AUTH_URL}/gap-tasks/${taskId}/transition`, {
        method: "POST",
        headers,
        body: JSON.stringify({ action, ...extra }),
      });
      if (res.ok) {
        await fetchTasks();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Transition failed: ${err.detail || res.status}`);
      }
    } catch {}
    setTransitioning((prev) => {
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  };

  // Inline quick actions (outside research panel)
  const quickAction = async (task, tr) => {
    if (tr.requiresDraft || tr.requiresReview) {
      setActiveTask(task);
      return;
    }
    await handleTransition(task.id, tr.action);
  };

  const filtered = tasks.filter((t) => {
    if (!search) return true;
    return t.question.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-800">
            Gap Tasks — Steward Workflow
          </h1>
          <p className="text-sm text-slate-500">
            Research, draft, and approve answers for KB knowledge gaps.
          </p>
        </div>
        <button
          onClick={fetchTasks}
          disabled={loading}
          className="px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Steward dashboard */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            {
              label: "My Assigned",
              value: stats.my_assigned ?? "—",
              color: "text-blue-700",
            },
            {
              label: "Awaiting Approval",
              value: stats.awaiting_approval ?? "—",
              color: "text-violet-700",
            },
            {
              label: "Stale Re-verifications",
              value: stats.stale ?? "—",
              color: "text-rose-700",
            },
            {
              label: "Avg. Research Time",
              value: stats.avg_research_time
                ? `${stats.avg_research_time}h`
                : "—",
              color: "text-slate-700",
            },
          ].map(({ label, value, color }) => (
            <div
              key={label}
              className="bg-white rounded-xl border border-slate-200 p-3"
            >
              <p className={`text-xl font-bold ${color}`}>{value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={facilityId}
          onChange={(e) => setFacilityId(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select facility...</option>
          {facilities.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>
              {f.facility_name || f.facility_id}
            </option>
          ))}
        </select>

        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All States</option>
          {Object.entries(STATES).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={myGapsOnly}
            onChange={(e) => setMyGapsOnly(e.target.checked)}
            className="rounded"
          />
          My Gaps
        </label>

        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search questions..."
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading gap tasks...
        </div>
      ) : !facilityId ? (
        <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
          Select a facility to view gap tasks.
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          <CheckCircle className="w-10 h-10 mx-auto mb-3 text-green-400" />
          <p className="font-medium text-slate-700">No gap tasks match</p>
          <p className="text-sm text-slate-400 mt-1">
            {stateFilter === "all"
              ? "All gaps are resolved."
              : `No tasks in "${STATES[stateFilter]?.label}" state.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((task) => {
            const transitions = TRANSITIONS[task.state] || [];
            const isTransitioning = transitioning.has(task.id);

            return (
              <div
                key={task.id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm"
              >
                <div
                  className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 rounded-t-xl"
                  onClick={() => setActiveTask(task)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <StateBadge state={task.state} />
                      <TimeInStateBadge task={task} />
                      {task.times_surfaced > 1 && (
                        <span className="text-xs text-slate-400">
                          {task.times_surfaced}× surfaced
                        </span>
                      )}
                      {task.confidence_floor != null && (
                        <span className="text-xs text-slate-400">
                          {Math.round(task.confidence_floor * 100)}% conf.
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-800 font-medium line-clamp-2">
                      {task.question}
                    </p>
                    {task.assigned_to_email && (
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <User className="w-3 h-3" /> {task.assigned_to_email}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                </div>

                {/* Quick action row */}
                {transitions.length > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2.5 border-t border-slate-100 bg-slate-50/60 rounded-b-xl">
                    {transitions.map((tr) => (
                      <button
                        key={tr.action}
                        onClick={() => quickAction(task, tr)}
                        disabled={isTransitioning}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1.5 disabled:opacity-50 ${
                          tr.color === "green"
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : tr.color === "red"
                              ? "bg-red-100 text-red-700 hover:bg-red-200"
                              : tr.color === "violet"
                                ? "bg-violet-100 text-violet-700 hover:bg-violet-200"
                                : tr.color === "amber"
                                  ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                  : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                        }`}
                      >
                        {isTransitioning ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <ArrowRight className="w-3 h-3" />
                        )}
                        {tr.label}
                      </button>
                    ))}
                    <button
                      onClick={() => setActiveTask(task)}
                      className="ml-auto text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                    >
                      Open research <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Research panel */}
      {activeTask && (
        <ResearchPanel
          task={activeTask}
          onClose={() => setActiveTask(null)}
          onTransition={handleTransition}
          isReviewer={isManager}
          headers={headers}
        />
      )}
    </div>
  );
};

export default GapTasksSteward;
